import { useEffect, useRef, useState } from "react";
import { usePreferenceStorage } from "./storage/PreferenceStorageContext";
import { useLogger } from "@/logging/client/LoggerContext";

const localAuthModule =
	import.meta.env.VITE_CAPACITOR_BUILD === "true"
		? import("@vcg/capacitor-local-auth")
		: null;

const MIN_TIME_BETWEEN_LOCAL_AUTH = 1000 * 60 * 5; // 5 minutes

export function useLocalAuth() {
	const preferences = usePreferenceStorage();
	const [isAuthenticated, setIsAuthenticated] = useState(
		import.meta.env.VITE_CAPACITOR_BUILD ? null : true,
	);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const checked = useRef(false);

	const logger = useLogger();

	useEffect(() => {
		async function waitForAppActive(): Promise<void> {
			// In Capacitor builds, ensure the app is active (foreground) before prompting
			if (import.meta.env.VITE_CAPACITOR_BUILD === "true") {
				try {
					const appModule = await import("@capacitor/app");
					const state = await appModule.App.getState();
					if (state?.isActive) return;
					await new Promise<void>((resolve) => {
						const remove = appModule.App.addListener(
							"appStateChange",
							async (s) => {
								if (s.isActive) {
									await remove;
									resolve();
								}
							},
						);
					});
				} catch {
					// Fallback to document visibility
				}
			}

			if (typeof document !== "undefined") {
				if (document.visibilityState === "visible") return;
				await new Promise<void>((resolve) => {
					const onChange = () => {
						if (document.visibilityState === "visible") {
							document.removeEventListener("visibilitychange", onChange);
							resolve();
						}
					};
					document.addEventListener("visibilitychange", onChange);
				});
			}
		}

		async function performAuthentication(): Promise<boolean> {
			if (!localAuthModule) {
				return true;
			}

			// Avoid iOS LAErrorUserInteractionNotAllowed by waiting for active state
			await waitForAppActive();

			const { LocalAuth } = await localAuthModule;

			const availability = await LocalAuth.checkAvailability();

			if (availability.available) {
				return await LocalAuth.authenticate({
					confirmationRequired: false,
					reason: "Authenticate to access your account",
					title: "Authenticate",
				}).then((result) => {
					if (result.success) {
						return true;
					}

					throw new Error(result.error ?? "Unknown error");
				});
			}

			logger.warn(
				`Device does not support local auth. available: ${availability.available} biometrics: ${availability.biometrics} deviceCredentials: ${availability.deviceCredentials}`,
			);

			// we have to handle the scenario where the device doesn't support local auth,
			// currently we're choosing to bypass local auth in this scenario.
			return true;
		}

		async function checkLocalAuth() {
			if (!localAuthModule) {
				return true;
			}

			const lastLocalAuth = await preferences
				.getItem("vcg-last-local-auth")
				.catch(() => null);

			if (!lastLocalAuth) {
				// first time the app is launched, we don't need to check local auth
				// but need to initialize the last local auth time for future loads
				preferences.setItem("vcg-last-local-auth", new Date().toISOString());

				return true;
			}

			if (
				lastLocalAuth >
				new Date(Date.now() - MIN_TIME_BETWEEN_LOCAL_AUTH).toISOString()
			) {
				// last auth was less than min time between auths
				return true;
			}

			return performAuthentication();
		}

		if (checked.current || loading || isAuthenticated) {
			return;
		}

		setLoading(true);
		checkLocalAuth()
			.then((isAuthenticated) => {
				if (isAuthenticated) {
					logger.info("Local auth successful");
					preferences.setItem("vcg-last-local-auth", new Date().toISOString());
				}
				setIsAuthenticated(isAuthenticated ?? false);
				setError(null);
			})
			.catch((error) => {
				logger.error(
					`Error checking local auth. Error: ${error instanceof Error ? error.message : error}`,
				);
				setError(error);
				setIsAuthenticated(false);
			})
			.finally(() => {
				checked.current = true;
				setLoading(false);
			});
	}, []);

	// Allow consumers to explicitly prompt for auth (e.g., when iOS reports "User interaction required")
	const prompt = async () => {
		try {
			setLoading(true);
			// Reset error state so UI can update
			setError(null);

			if (!localAuthModule) {
				setIsAuthenticated(true);
				return;
			}

			const { LocalAuth } = await localAuthModule;
			const availability = await LocalAuth.checkAvailability();
			if (!availability.available) {
				setIsAuthenticated(true);
				return;
			}

			const result = await LocalAuth.authenticate({
				confirmationRequired: false,
				reason: "Authenticate to access your account",
				title: "Authenticate",
			});
			if (result.success) {
				await preferences.setItem(
					"vcg-last-local-auth",
					new Date().toISOString(),
				);
				setIsAuthenticated(true);
				setError(null);
			} else {
				throw new Error(result.error ?? "Unknown error");
			}
		} catch (e) {
			logger.error(
				`Error prompting local auth. Error: ${e instanceof Error ? e.message : e}`,
			);
			setError(e as Error);
			setIsAuthenticated(false);
		} finally {
			setLoading(false);
		}
	};

	return { isAuthenticated, loading, error, prompt };
}
